import { Server, CheckCircle2, AlertTriangle, RefreshCw, Layers } from 'lucide-react';
import { cn } from '@/lib/cn';
import { opsApi } from '@/lib/ops-api';
import { usePolling } from '@/hooks/usePolling';
import TeamCard from '@/components/TeamCard';
import type { OverviewResponse } from '@/lib/types';

export default function OverviewPage() {
  const { data, error, loading, refresh } = usePolling<OverviewResponse>(
    () => opsApi.getOverview(),
    30_000,
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">
            Overview
          </h1>
          <p className="text-surface-500 text-sm mt-1">
            Real-time health across all deployments
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-surface-400',
            'bg-white/[0.03] border border-white/[0.06]',
            'hover:text-surface-200 hover:bg-white/[0.05] transition-all',
            'disabled:opacity-50',
          )}
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Summary metrics */}
      {loading && !data ? (
        <MetricsSkeleton />
      ) : data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            icon={<Layers className="w-5 h-5" />}
            label="Teams"
            value={data.summary.team_count}
            color="primary"
          />
          <MetricCard
            icon={<Server className="w-5 h-5" />}
            label="Total Services"
            value={data.summary.total_services}
            color="primary"
          />
          <MetricCard
            icon={<CheckCircle2 className="w-5 h-5" />}
            label="Healthy"
            value={data.summary.healthy_services}
            color="healthy"
          />
          <MetricCard
            icon={<AlertTriangle className="w-5 h-5" />}
            label="Unhealthy"
            value={data.summary.unhealthy_services}
            color={data.summary.unhealthy_services > 0 ? 'critical' : 'healthy'}
          />
        </div>
      ) : null}

      {/* Teams grid */}
      {loading && !data ? (
        <TeamGridSkeleton />
      ) : data && data.teams.length > 0 ? (
        <div>
          <h2 className="text-sm font-medium text-surface-400 uppercase tracking-wider mb-4">
            Deployments ({data.teams.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.teams.map((team, i) => (
              <TeamCard key={team.team_id} team={team} index={i} />
            ))}
          </div>
        </div>
      ) : data ? (
        <div className="text-center py-16">
          <Server className="w-12 h-12 text-surface-600 mx-auto mb-4" />
          <p className="text-surface-400">No teams found</p>
        </div>
      ) : null}
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'primary' | 'healthy' | 'critical';
}) {
  return (
    <div className="metric-card flex items-center gap-4">
      <div
        className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
          color === 'primary' && 'bg-primary-500/15 text-primary-400',
          color === 'healthy' && 'bg-status-healthy/15 text-status-healthy',
          color === 'critical' && 'bg-status-critical/15 text-status-critical',
        )}
      >
        {icon}
      </div>
      <div>
        <p className="text-2xl font-display font-bold text-white leading-none">
          {value}
        </p>
        <p className="text-xs text-surface-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function MetricsSkeleton() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="metric-card animate-pulse">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-surface-700" />
            <div className="space-y-2">
              <div className="h-6 w-12 bg-surface-700 rounded" />
              <div className="h-3 w-16 bg-surface-800 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TeamGridSkeleton() {
  return (
    <div>
      <div className="h-4 w-32 bg-surface-800 rounded mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="rounded-2xl bg-white/[0.02] border border-white/[0.06] p-5 animate-pulse">
            <div className="flex items-start justify-between mb-4">
              <div className="space-y-2 flex-1">
                <div className="h-5 w-24 bg-surface-700 rounded" />
                <div className="h-3 w-36 bg-surface-800 rounded" />
              </div>
              <div className="h-6 w-16 bg-surface-700 rounded-full" />
            </div>
            <div className="h-1.5 w-full bg-surface-800 rounded-full mb-4" />
            <div className="grid grid-cols-3 gap-3">
              {[...Array(3)].map((_, j) => (
                <div key={j} className="h-10 bg-surface-800/50 rounded-lg" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
